import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, CheckCircle, ChevronRight } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_vets-intake-demo/artifacts/e4mtecfo_Untitled%20design%20%2892%29.png";

export const ContactPage = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const encode = (data) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "contact", ...formData })
    })
      .then(() => {
        setIsSubmitted(true);
        setIsSubmitting(false);
      })
      .catch((error) => {
        console.error("Form submission error:", error);
        setIsSubmitting(false);
        alert("There was an error submitting the form. Please try again.");
      });
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'info@worthyvetsservicegroup.com',
      href: 'mailto:info@worthyvetsservicegroup.com'
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '(571) 600-6706',
      href: 'tel:+15716006706'
    },
    {
      icon: MapPin,
      label: 'Address',
      value: '23710 Schooler Plaza Suite 275, Brambleton, VA 20148',
      href: null
    },
    {
      icon: Clock,
      label: 'Response Time',
      value: 'Within 1-2 Business Days',
      href: null
    }
  ];

  return (
    <div className="page-transition" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-wv-blue py-20" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-wv-red text-lg font-semibold mb-4 tracking-wide uppercase">
              Contact Us
            </p>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6 uppercase tracking-wide">
              Get In Touch
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              Have questions about our services or ready to get started? We are here to help. 
              Reach out and we will respond as quickly as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-paper-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-wv-blue mb-8">
                Contact Information
              </h2>
              
              <div className="flex justify-center mb-8">
                <img 
                  src={LOGO_URL} 
                  alt="Worthy Vets Service Group" 
                  className="h-24 w-auto"
                  loading="lazy"
                />
              </div>
              
              <div className="space-y-6 mb-12">
                {contactInfo.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="w-14 h-14 bg-wv-red rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 uppercase tracking-wide font-semibold">
                        {item.label}
                      </p>
                      {item.href ? (
                        <a 
                          href={item.href}
                          className="text-xl text-wv-blue font-semibold hover:text-wv-red transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-xl text-wv-blue font-semibold">
                          {item.value}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-lg p-8 border-2 border-gray-100">
                <h3 className="font-heading text-2xl font-bold text-wv-blue mb-4">
                  Ready to Start Your Claim?
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  If you are ready to get assistance with your VA disability claim, 
                  our intake form is the fastest way to get started.
                </p>
                <Link
                  to="/intake"
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="contact-intake-link"
                >
                  Start Intake Form
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-wv-blue mb-8">
                Send Us a Message
              </h2>

              {isSubmitted ? (
                <div 
                  className="bg-white rounded-lg p-12 text-center border-2 border-wv-red"
                  data-testid="contact-success"
                >
                  <CheckCircle className="w-16 h-16 text-wv-red mx-auto mb-6" />
                  <h3 className="font-heading text-2xl font-bold text-wv-blue mb-4">
                    Message Sent!
                  </h3>
                  <p className="text-lg text-gray-700 mb-6">
                    Thank you for reaching out. We will get back to you within 1-2 business days.
                  </p>
                  <button
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
                    }}
                    className="btn-secondary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  onSubmit={handleSubmit}
                  className="bg-white rounded-lg p-8 md:p-12 shadow-lg border-2 border-gray-100"
                  data-testid="contact-form"
                >
                  <input type="hidden" name="form-name" value="contact" />
                  <p className="hidden">
                    <label>Do not fill this out: <input name="bot-field" /></label>
                  </p>

                  <div className="mb-6">
                    <label htmlFor="name" className="form-label">
                      Full Name <span className="text-wv-red">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Your full name"
                      className="form-input w-full"
                      data-testid="input-name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="mb-6">
                      <label htmlFor="email" className="form-label">
                        Email <span className="text-wv-red">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your.email@example.com"
                        className="form-input w-full"
                        data-testid="input-email"
                      />
                    </div>

                    <div className="mb-6">
                      <label htmlFor="phone" className="form-label">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(555) 123-4567"
                        className="form-input w-full"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="subject" className="form-label">
                      Subject <span className="text-wv-red">*</span>
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="form-input w-full cursor-pointer"
                      data-testid="select-subject"
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="services">Question About Services</option>
                      <option value="claims">Claims Assistance</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="mb-8">
                    <label htmlFor="message" className="form-label">
                      Message <span className="text-wv-red">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      placeholder="How can we help you?"
                      className="form-input w-full resize-none py-4"
                      data-testid="textarea-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`btn-primary w-full flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    {!isSubmitting && <ChevronRight className="w-5 h-5" />}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-16 bg-white" data-testid="faq-teaser">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-wv-blue mb-4">
            Have Questions About Our Services?
          </h3>
          <p className="text-lg text-gray-600 mb-6">
            Check out our Services page for detailed information and frequently asked questions.
          </p>
          <Link
            to="/services"
            className="btn-secondary inline-flex items-center"
            data-testid="services-link"
          >
            View Services & FAQ
            <ChevronRight className="w-5 h-5 ml-1" />
          </Link>
        </div>
      </section>
    </div>
  );
};
